import { decamelize } from 'humps';
import axios from './axios';
import { BillingEngineChargesResponse } from '../types/billingEngine';
import { APIBuyerCreditCheck } from '../types/buyerCreditCheck';
import { APIInsuranceRequest } from '../types/insurance';
import { APILLC, LLCDocument, LLCProductRequest, LLCVerificationReadiness } from '../types/llc';
import { APIStakeholder } from '../types/documents';
import { APIFXRequest } from '../types/fxRequest';
import { APIAsset, ApplicationFlowType, StakeholderAsset } from '../types/application';
import { Feedback, Language, Log, PaginatedResponse, PaginatedResponseDynamoDB } from './types';
import {
  AdminCreateCompanyPayload,
  Company,
  CompanyAdminResult,
  CompanyUserInvitation,
  ProductStatus,
  UserRole,
} from '../types/companyUser';
import { Nullable, UserGroups } from '../types';
import { CreditCommitteeFlow, CreditLineDetails, DebtorsSheetPreview } from '../types/creditCommitteeFlow';
import {
  GlobalAccount,
  GlobalAccountCreationOption,
  ConnectedAccount,
  CashControlFeeConfigDetail,
} from '../types/banking';
import { getCountryKey } from '../store';
import { FACTORING_NEW_SOA_ASSET_TYPE } from '../constants/common';
import { FundingRequestSOADocuments } from '../types/documents';
import { LenderTransferResults } from '../types/cashControlLender';
import {
  CreateCreditLinePayload,
  CreateManualPaymentPayload,
  CreditLine,
  CreditLineDetail,
  CreditLinesResponse,
  EditCreditLinePayload,
  InterestPaymentsResponse,
  InterestPaymentDetail,
  Payment,
  PaymentsResponse,
  PaymentDetail,
  WithdrawalsResponse,
  WithdrawalDetail,
} from '../types/revolvingCreditLine';
import {
  AdminFactoringInvoice,
  AdminFactoringSoldInvoice,
  FactoringInvoiceStatusFilter,
  InvoicesTabState,
} from '../types/factoringCreditLine';

export function adminGetBuyerCreditCheckByCompanyUserId(id: number): Promise<APIBuyerCreditCheck[]> {
  return axios
    .get(`/admins/companies/${id}/products/buyer_credit_check/requests/`)
    .then(({ data }) => data || []);
}

export function adminGetInsuranceRequestByCompanyUserId(id: number): Promise<APIInsuranceRequest[]> {
  return axios.get(`/admins/companies/${id}/products/insurances/requests/`).then(({ data }) => data || []);
}

export function adminGetFXRequestByCompanyUserId(id: number): Promise<APIFXRequest[]> {
  return axios.get(`/admins/companies/${id}/products/fx/requests/`).then(({ data }) => data || []);
}

export function changeApplicationFlow(id: number, flowType: ApplicationFlowType, companyId: number) {
  return axios
    .post(`/admins/companies/${companyId}/products/factoring/request/${id}/set_flow_type/`, {
      flowType,
    })
    .then((data) => data);
}

export function logInAsCompanyUser(id: number) {
  return axios.get(`/admins/company_users/${id}/impersonation_token/`).then(({ data }) => data);
}

export function adminGetLLCProductRequest(companyId: number) {
  return axios
    .get<LLCProductRequest[]>(`/admins/companies/${companyId}/products/llc/company_llc/request/`)
    .then(({ data }) => data);
}

export function adminGetLLC(companyId: number) {
  return axios
    .get<APILLC[]>(`/admins/companies/${companyId}/products/llc/company_llc/`)
    .then(({ data }) => data);
}

export function adminCreateLLC(companyId: number) {
  return axios
    .post<APILLC>(`/admins/companies/${companyId}/products/llc/company_llc/`)
    .then(({ data }) => data);
}

export function adminUploadLLCAsset(
  llcId: number,
  documentId: number,
  { files }: { files: File[] },
  companyId: number
) {
  const payload = new FormData();
  files.forEach((file) => {
    payload.append('asset', file, file.name);
    payload.append('file_name', file.name);
    payload.append('asset_type', 'document');
  });

  return axios
    .post<LLCDocument>(
      `/admins/companies/${companyId}/products/llc/company_llc/${llcId}/documents/${documentId}/assets/`,
      payload,
      {
        headers: {
          'Content-Type': `multipart/form-data; boundary=BoUnDaRyStRiNg`,
        },
      }
    )
    .then(({ data }) => data);
}

export function adminDeleteLLCDocumentFile(assetId: number) {
  return axios.delete<any>(`/assets/${assetId}/`).then(({ data }) => data);
}

export function adminAddNewDocument(companyId: number, llcId: number, values: any, files: File[]) {
  const payload = new FormData();
  files.forEach((file) => {
    payload.append('asset', file, file.name);
    payload.append('file_name', file.name);
    payload.append('asset_type', 'document');
  });

  payload.append('title_en', values.titleEn);
  payload.append('title_es', values.titleEs);
  payload.append('description_en', values.descriptionEn);
  payload.append('description_es', values.descriptionEs);
  payload.append('notify_user', values.notifyUser);

  return axios
    .post<LLCDocument>(
      `/admins/companies/${companyId}/products/llc/company_llc/${llcId}/generic_documents/`,
      payload,
      {
        headers: {
          'Content-Type': `multipart/form-data; boundary=BoUnDaRyStRiNg`,
        },
      }
    )
    .then(({ data }) => data);
}

export function adminDeleteGenericDocument(companyId: number, llcId: number, documentId: number) {
  return axios
    .delete<any>(
      `/admins/companies/${companyId}/products/llc/company_llc/${llcId}/generic_documents/${documentId}/`
    )
    .then(({ data }) => data);
}

export function adminSetCompanyUUID(companyId: number, payload: { uuid: string }) {
  return axios.post<Company>(`/admins/companies/${companyId}/set_uuid/`, payload).then(({ data }) => data);
}

export type QaAdminResult = {
  email: string;
  userId: number;
  companyId: number;
  applicationId: number;
  companyUuid: number;
};

export function adminCreateQaUser(payload: {
  country: string;
  password: string;
  invoiceFinancingStep: string;
}) {
  return axios
    .post<QaAdminResult>(`/admins/company_users/create_qa_account/`, {
      ...payload,
      factoringStep: payload.invoiceFinancingStep,
    })
    .then(({ data }) => data);
}

export function adminCheckCompanyExists(companyId: number) {
  return axios
    .get<{
      CompanyUserCompanyUuidExists: boolean;
      emailIdExists: boolean;
    }>(`/admins/companies/${companyId}/products/factoring/factoring_data/company_exists_in_lms/`)
    .then(({ data }) => data);
}

export function getCompanies(company: string) {
  return axios
    .get<PaginatedResponse<CompanyAdminResult>>(`/admins/companies/list/?company=${company}`)
    .then(({ data }) => data);
}

export function connectInvoiceWithShipment(payload: any) {
  return axios
    .post(`/admins/products/tracking/invoice_container_tracking_connection/`, payload)
    .then(({ data }) => data);
}

export function adminGetUserGroups() {
  return axios.get<UserGroups>(`/admins/credit_committee/groups/`).then(({ data }) => data);
}

export function adminGetPostCCFlows({
  status,
  page,
  pageSize,
}: {
  status: 'in_progress' | 'complete';
  page: number;
  pageSize: number;
}) {
  return axios
    .get<PaginatedResponse<CreditCommitteeFlow>>(
      `/admins/credit_committee/?status=${status}&page=${page}&page_size=${pageSize}`
    )
    .then(({ data }) => data);
}

export function adminGetOpportunitiesByName(companyName: string) {
  return axios
    .get<{ opportunityId: string; companyName: string; companyUuid: string }[]>(
      `/admins/credit_committee/opportunities?company_name=${companyName}`
    )
    .then(({ data }) => data);
}

export function adminGetPostCCFlowPreview(opportunityId: string, spreadsheetLink: string) {
  const formattedSpreadsheetLink = spreadsheetLink.split('#')[0];
  return axios
    .get<CreditCommitteeFlow>(
      `/admins/credit_committee/previsualize/structuring_spreadsheet/?spreadsheet_link=${formattedSpreadsheetLink}&opportunity_id=${opportunityId}`
    )
    .then(({ data }) => data);
}

export function adminCreateCreditCommitteeFlow(payload: CreditCommitteeFlow) {
  return axios.post<CreditCommitteeFlow>(`/admins/credit_committee/`, payload).then(({ data }) => data);
}

export function adminGetCcFlowDetail(id: string) {
  return axios.get<CreditCommitteeFlow>(`/admins/credit_committee/${id}/`).then(({ data }) => data);
}

export function adminGetCreditLineDetails(opportunityId: string) {
  return axios
    .get<CreditLineDetails>(`/admins/credit_committee/opportunity/${opportunityId}/credit_line_details`)
    .then(({ data }) => data);
}

export function adminGetDebtorsSheetPreview(spreadsheetLink: string) {
  return axios
    .get<DebtorsSheetPreview>(
      `/admins/credit_committee/previsualize/debtors_spreadsheet/?spreadsheet_link=${spreadsheetLink}`
    )
    .then(({ data }) => data);
}

export function adminCreateCCDebtors(
  ccFlowId: string,
  payload: {
    debtors: DebtorsSheetPreview['debtors'];
    debtorsSpreadsheetUrl: string;
    creditLineDetails: Partial<CreditLineDetails>;
  }
) {
  // remove null values from creditLineDetails
  const creditLineDetails = Object.entries(payload.creditLineDetails).reduce((acc, [key, value]) => {
    if (value !== null) {
      // @ts-ignore
      acc[key] = value;
    }
    return acc;
  }, {} as Partial<CreditLineDetails>);
  return axios
    .post(`/admins/credit_committee/${ccFlowId}/debtors`, {
      ...payload,
      creditLineDetails: {
        ...creditLineDetails,
        discount_fee_tier_1: creditLineDetails.discountFeeTier1,
        discount_fee_tier_2: creditLineDetails.discountFeeTier2,
      },
    })
    .then(({ data }) => data);
}

export function adminApproveMinute(ccFlowId: string) {
  return axios.post(`/admins/credit_committee/${ccFlowId}/approve_minute`).then(({ data }) => data);
}

export function adminRejectMinute(ccFlowId: string) {
  return axios.post(`/admins/credit_committee/${ccFlowId}/reject_minute`).then(({ data }) => data);
}

export function adminGetUserInvitations(companyId: number) {
  return axios
    .get<CompanyUserInvitation[]>(`/admins/companies/${companyId}/user_invitations/list/`)
    .then(({ data }) => data);
}

export function adminEditCompanyUserRole(
  companyId: number,
  companyUserRoleId: number,
  values: { role?: UserRole; jobTitle?: string }
) {
  return axios
    .patch(`/admins/companies/${companyId}/company_user_roles/${companyUserRoleId}/`, values)
    .then(({ data }) => data);
}

export function adminInviteUser(companyId: number, values: any) {
  return axios.post(`/admins/companies/${companyId}/user_invitations/`, values).then(({ data }) => data);
}

export function adminDeleteCompanyUser(companyId: number, companyUserRoleId: number) {
  return axios
    .delete(`/admins/companies/${companyId}/company_user_roles/${companyUserRoleId}/`)
    .then(({ data }) => data);
}

export function adminDeleteInvitation(companyId: number, invitationId: number) {
  return axios
    .delete(`/admins/companies/${companyId}/user_invitations/${invitationId}/`)
    .then(({ data }) => data);
}

export function adminResendInvitation(companyId: number, invitationId: number): Promise<any> {
  return axios
    .post(`/admins/companies/${companyId}/user_invitations/${invitationId}/resend/`)
    .then(({ data }) => data);
}

export function adminGetLinkedAccount(companyId: number, accountId: number) {
  return axios
    .get<{ linkedAccount: Nullable<ConnectedAccount>; globalAccounts: GlobalAccount[] }>(
      `/admins/companies/${companyId}/products/banking/accounts/${accountId}/`
    )
    .then(({ data }) => data);
}

export function adminLinkAccount(companyId: number, payload: { companyName: string; accountId: string }) {
  return axios
    .post<ConnectedAccount>(`/admins/companies/${companyId}/products/banking/link_account/`, payload)
    .then(({ data }) => data);
}

export function adminEnableAccountCreation(companyId: number, productStatusId: number) {
  return axios
    .post<ProductStatus>(
      `admins/companies/${companyId}/products/product_status/${productStatusId}/toggle_ready_for_banking_account_creation/`
    )
    .then(({ data }) => data);
}

export function adminEnableBanking(companyId: number, productStatusId: number) {
  return axios
    .post<ProductStatus>(
      `admins/companies/${companyId}/products/product_status/${productStatusId}/toggle_company_ready_for_banking/`
    )
    .then(({ data }) => data);
}

export function adminEnableShowRcl(companyId: number, productStatusId: number) {
  return axios
    .post<ProductStatus>(
      `admins/companies/${companyId}/products/product_status/${productStatusId}/toggle_company_show_rcl/`
    )
    .then(({ data }) => data);
}

export function adminEnableShowFactoringCreditLine(companyId: number, productStatusId: number) {
  return axios
    .post<ProductStatus>(
      `admins/companies/${companyId}/products/product_status/${productStatusId}/toggle_company_show_factoring_credit_line/`
    )
    .then(({ data }) => data);
}

export function adminDisableGlobalAccount(accountId: number, id: number) {
  return axios
    .post<GlobalAccount>(`/admins/products/banking/accounts/${accountId}/global_accounts/${id}/disable/`)
    .then(({ data }) => data);
}

export function adminGetGlobalAccountCreationOptions(accountId: number) {
  return axios
    .get<GlobalAccountCreationOption[]>(
      `/admins/products/banking/accounts/${accountId}/global_accounts/creation_options/`
    )
    .then(({ data }) => data);
}

export function adminGetGlobalAccountDetail(accountId: number, id: number) {
  return axios
    .get<GlobalAccount>(`/admins/products/banking/accounts/${accountId}/global_accounts/${id}/`)
    .then(({ data }) => data);
}

export function adminUnlinkAccount(companyId: number, accountId: number) {
  return axios
    .delete(`/admins/companies/${companyId}/products/banking/accounts/${accountId}/unlink_account/`)
    .then(({ data }) => data);
}

export function adminGetAuditLogs(filters?: any): Promise<PaginatedResponse<Log>> {
  return axios
    .get<PaginatedResponse<Log>>(`/admins/audit_logs/`, { params: filters || {} })
    .then(({ data }) => data);
}

export function adminCreateLLCStakeholder(
  companyId: number,
  llcId: number,
  values: Record<string, unknown>,
  files: StakeholderAsset[] = []
) {
  // The axios interceptor skips FormData, so the keys are snake_cased here or the
  // serializer silently drops every field it does not recognise
  const data = new FormData();
  Object.entries(values).forEach(([key, value]) => {
    data.append(decamelize(key), value === null || value === undefined ? '' : String(value));
  });
  files.forEach(({ file, assetType }) => {
    data.append('asset', file, file.name);
    data.append('file_name', file.name);
    data.append('asset_type', assetType);
  });
  return axios
    .post(`/admins/companies/${companyId}/products/llc/company_llc/${llcId}/stakeholders/`, data)
    .then(({ data }) => data);
}

export function adminUpdateLLCStakeholder(
  companyId: number,
  llcId: number,
  stakeholderId: number,
  values: Record<string, unknown>
) {
  return axios
    .patch(
      `/admins/companies/${companyId}/products/llc/company_llc/${llcId}/stakeholders/${stakeholderId}/`,
      values
    )
    .then(({ data }) => data);
}

export function adminDeleteLLCStakeholder(companyId: number, llcId: number, stakeholderId: number) {
  return axios.delete(
    `/admins/companies/${companyId}/products/llc/company_llc/${llcId}/stakeholders/${stakeholderId}/`
  );
}

export function adminUploadLLCStakeholderAssets(
  companyId: number,
  llcId: number,
  stakeholderId: number,
  files: StakeholderAsset[]
): Promise<APIStakeholder> {
  const data = new FormData();
  files.forEach(({ file, assetType }) => {
    data.append('asset', file, file.name);
    data.append('file_name', file.name);
    data.append('asset_type', assetType);
  });
  return axios
    .post(
      `/admins/companies/${companyId}/products/llc/company_llc/${llcId}/stakeholders/${stakeholderId}/assets/`,
      data
    )
    .then(({ data }) => data);
}

export function adminDeleteLLCStakeholderAsset(
  companyId: number,
  llcId: number,
  stakeholderId: number,
  assetId: number
) {
  return axios.delete(
    `/admins/companies/${companyId}/products/llc/company_llc/${llcId}/stakeholders/${stakeholderId}/assets/${assetId}/`
  );
}

export function getLLCVerificationReadiness(companyId: number, companyLLC: number) {
  return axios
    .get<LLCVerificationReadiness>(
      `/admins/companies/${companyId}/products/llc/company_llc/${companyLLC}/verification/`
    )
    .then(({ data }) => data);
}

export function startLLCVerification(companyId: number, companyLLC: number) {
  return axios
    .post<LLCVerificationReadiness>(
      `/admins/companies/${companyId}/products/llc/company_llc/${companyLLC}/verification/`
    )
    .then(({ data }) => data);
}

export function adminCreateCompany(payload: AdminCreateCompanyPayload) {
  return axios.post<Company>(`/admins/companies/create/`, payload).then(({ data }) => data);
}

export function adminResendPasswordEmail(companyId: number, email: string) {
  return axios.post(`/admins/companies/${companyId}/resend_password_email/`, { email });
}

export function updateLLCInformation(companyId: number, companyLLC: number, values: any) {
  return axios
    .patch(`/admins/companies/${companyId}/products/llc/company_llc/${companyLLC}/`, values)
    .then(({ data }) => data);
}

export function notifyCompanyLLCDocument(companyId: number, companyLLC: number, documentId: number) {
  return axios
    .post(
      `/admins/companies/${companyId}/products/llc/company_llc/${companyLLC}/documents/${documentId}/notify/`
    )
    .then(({ data }) => data);
}

export function notifyCompanyLLCGenericDocument(companyId: number, companyLLC: number, documentId: number) {
  return axios
    .post(
      `/admins/companies/${companyId}/products/llc/company_llc/${companyLLC}/generic_documents/${documentId}/notify/`
    )
    .then(({ data }) => data);
}

export function updateCompanyLLCDocument(
  companyId: number,
  companyLLC: number,
  documentId: number,
  payload: { signLink?: string; status: string }
) {
  return axios
    .patch(
      `/admins/companies/${companyId}/products/llc/company_llc/${companyLLC}/documents/${documentId}/`,
      payload
    )
    .then(({ data }) => data);
}

export function updateCompanyInformation(companyId: number, values: any): Promise<Company> {
  return axios.patch(`/admins/companies/${companyId}/update_company/`, values).then(({ data }) => ({
    ...data,
    country: getCountryKey(data.country),
  }));
}

export function adminGetFeedbackList(filters?: any): Promise<PaginatedResponseDynamoDB<Feedback>> {
  return axios
    .get<PaginatedResponseDynamoDB<Feedback>>(`/admins/feedback/feedback/`, { params: filters || {} })
    .then(({ data }) => data);
}

export function adminUpdateFeedback(feedback_id: string, values: any) {
  return axios.post(`/admins/feedback/feedback/${feedback_id}/`, values).then(({ data }) => data);
}

export function adminGetFeedbackDetail(id: string) {
  return axios.get<Feedback>(`/admins/feedback/feedback/${id}/`).then(({ data }) => data);
}

export function adminGetSignedSOADocuments(companyId: number, search: string) {
  return axios
    .get<FundingRequestSOADocuments>(
      `/admins/companies/${companyId}/products/factoring/funding_request_flow/signed_soa_assets/`,
      {
        params: { search },
      }
    )
    .then(({ data }) => data);
}

export function adminAddNewSOADocument(companyId: number, flowId: number, file: File) {
  const payload = new FormData();
  payload.append('asset', file, file.name);
  payload.append('file_name', file.name);
  payload.append('asset_type', FACTORING_NEW_SOA_ASSET_TYPE);

  return axios
    .post<APIAsset[]>(
      `/admins/companies/${companyId}/products/factoring/funding_request_flow/${flowId}/add_signed_soa_asset/`,
      payload,
      {
        headers: {
          'Content-Type': `multipart/form-data; boundary=BoUnDaRyStRiNg`,
        },
      }
    )
    .then(({ data }) => data);
}

export function adminUploadUnsignedMPSA(companyId: number, file: File) {
  const payload = new FormData();
  payload.append('asset', file, file.name);
  payload.append('file_name', file.name);
  payload.append('asset_type', 'unsigned_mpsa');

  return axios
    .post<APIAsset[]>(`/admins/companies/${companyId}/documents/upload_unsigned_mpsa/`, payload, {
      headers: {
        'Content-Type': `multipart/form-data; boundary=BoUnDaRyStRiNg`,
      },
    })
    .then(({ data }) => data);
}

export function adminGetUnsignedMPSA(companyId: number) {
  return axios
    .get<APIAsset[]>(`/admins/companies/${companyId}/documents/get_unsigned_mpsa/`)
    .then(({ data }) => data);
}

export function adminDeleteUnsignedMPSA(companyId: number, assetId: number) {
  return axios
    .delete(`/admins/companies/${companyId}/documents/delete_unsigned_mpsa/${assetId}/`)
    .then(({ data }) => data);
}

export function adminCreateCashControlAccount(companyId: number): Promise<{ url: string }> {
  return axios
    .post<{ url: string }>(`/admins/companies/${companyId}/products/banking/cash_control_account/create/`)
    .then(({ data }) => data);
}

export function adminGetCashControlAccount(companyId: number): Promise<Nullable<ConnectedAccount>> {
  return axios
    .get<ConnectedAccount>(`/admins/companies/${companyId}/products/banking/cash_control_account/`)
    .then(({ data }) => data);
}

export function adminUpdateCashControlAccountLender(
  companyId: number,
  lenderId: number | null,
  platformFee?: string,
  monthlyFee?: string
): Promise<any> {
  return axios
    .patch(`/admins/companies/${companyId}/products/banking/cash_control_account/lender/`, {
      lenderId,
      ...(platformFee !== undefined && { platformFee }),
      ...(monthlyFee !== undefined && { monthlyFee }),
    })
    .then(({ data }) => data);
}

export function adminGetCashControlFeeConfig(companyId: number): Promise<CashControlFeeConfigDetail> {
  return axios
    .get(`/admins/companies/${companyId}/products/banking/cash_control_account/lender/fee-config/`)
    .then(({ data }) => data);
}

export function adminRemoveCashControlLender(companyId: number): Promise<any> {
  return axios
    .delete(`/admins/companies/${companyId}/products/banking/cash_control_account/lender/`)
    .then(({ data }) => data);
}

export function adminGetCashControlLenders(params: {
  page: number;
  pageSize: number;
  search?: string;
}): Promise<{ count: number; results: any[] }> {
  const query = new URLSearchParams();
  query.append('page', String(params.page + 1));
  query.append('page_size', String(params.pageSize));
  if (params.search) query.append('search', params.search);
  return axios
    .get(`/admins/products/banking/cash-control/lenders/?${query.toString()}`)
    .then(({ data }) => data);
}

export function adminGetCashControlLenderDetail(lenderId: number): Promise<any> {
  return axios.get(`/admins/products/banking/cash-control/lenders/${lenderId}/`).then(({ data }) => data);
}

export function adminCreateCashControlLender(payload: any): Promise<any> {
  return axios.post('/admins/products/banking/cash-control/lenders/', payload).then(({ data }) => data);
}

export function adminUpdateCashControlLender(lenderId: number, payload: any): Promise<any> {
  return axios
    .patch(`/admins/products/banking/cash-control/lenders/${lenderId}/`, payload)
    .then(({ data }) => data);
}

export function adminTransferToLender(lenderId: number): Promise<LenderTransferResults> {
  return axios
    .post(`/admins/products/banking/cash-control/lenders/${lenderId}/transfer/`)
    .then(({ data }) => data);
}

export function adminGetCashControlLenderEmbeddedAuth(): Promise<any> {
  return axios.get('/admins/products/banking/cash-control/lenders/embedded-auth/').then(({ data }) => data);
}

export function adminCreateUserForLender(lenderId: number, payload: { language: Language }): Promise<any> {
  return axios
    .post(`/admins/products/banking/cash-control/lenders/${lenderId}/create-user/`, payload)
    .then(({ data }) => data);
}

export function adminExportGlobalAccountStatement(accountId: number, globalAccountId: string): Promise<Blob> {
  return axios
    .get(
      `/admins/products/banking/accounts/${accountId}/global_accounts/${globalAccountId}/export_statement/`,
      {
        responseType: 'blob',
      }
    )
    .then(({ data }) => data);
}

export function adminGetCreditLines(page: number, pageSize: number, filters: any) {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  filters.search && params.append('search', filters.search);
  filters.status && params.append('status', filters.status);

  return axios
    .get<CreditLinesResponse>(`/admins/products/rcl/credit-lines/?${params.toString()}`)
    .then(({ data }) => data);
}

// Contract G: admin factoring endpoints (Platform factoring app, IsAdmin). company_id is the
// Platform company id, sourced from the RCL credit line's client.providerClientId.
export function adminGetFactoringSoldInvoices(
  companyId: string,
  page: number,
  pageSize: number,
  status?: FactoringInvoiceStatusFilter
): Promise<PaginatedResponse<AdminFactoringSoldInvoice>> {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  if (status && status !== FactoringInvoiceStatusFilter.ALL) {
    params.append('status', status);
  }
  return axios
    .get(`/admins/products/factoring-credit-line/companies/${companyId}/sold-invoices/?${params.toString()}`)
    .then(({ data }) => data);
}

export function adminExportFactoringInvoiceReport(companyId: string): Promise<Blob> {
  return axios
    .get(`/admins/products/factoring-credit-line/companies/${companyId}/invoice-report/`, {
      responseType: 'blob',
    })
    .then(({ data }) => data);
}

// Cross-company list of every invoice Marco bought (sold + expired), for the admin
// Invoices tab. Filterable by credit line (rcl_credit_line_id), receiver (search) and a
// sold-date range.
export function adminGetFactoringInvoices(
  page: number,
  pageSize: number,
  filters: InvoicesTabState['filters']
): Promise<PaginatedResponse<AdminFactoringInvoice>> {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  filters.search && params.append('search', filters.search);
  filters.rclCreditLineId && params.append('rcl_credit_line_id', filters.rclCreditLineId);
  if (filters.status && filters.status !== FactoringInvoiceStatusFilter.ALL) {
    params.append('status', filters.status);
  }
  if (filters.fromDate) {
    params.append('start_date', filters.fromDate.startOf('day').toISOString());
  }
  if (filters.toDate) {
    params.append('end_date', filters.toDate.endOf('day').toISOString());
  }

  return axios
    .get(`/admins/products/factoring-credit-line/sold-invoices/?${params.toString()}`)
    .then(({ data }) => data);
}

// Mark an invoice as "not advanced" with optional admin/client justifications.
export function adminMarkInvoiceNotAdvanced(
  invoiceId: string,
  reasons: { adminReason: string; clientReason: string }
): Promise<AdminFactoringInvoice> {
  return axios
    .post(`/admins/products/factoring-credit-line/invoices/${invoiceId}/not-advanced/`, {
      adminReason: reasons.adminReason,
      clientReason: reasons.clientReason,
    })
    .then(({ data }) => data);
}

// Revert the "not advanced" flag; the backend clears both justifications.
export function adminRevertInvoiceNotAdvanced(invoiceId: string): Promise<AdminFactoringInvoice> {
  return axios
    .delete(`/admins/products/factoring-credit-line/invoices/${invoiceId}/not-advanced/`)
    .then(({ data }) => data);
}

export function adminExportCreditLines(): Promise<Blob> {
  return axios
    .get(`/admins/products/rcl/credit-lines/reports/list/`, {
      responseType: 'blob',
    })
    .then(({ data }) => data);
}

export function adminExportLongTapeReport(filters: {
  companyId?: string;
  dates?: { fromDate: any; toDate: any };
}): Promise<Blob> {
  const params = new URLSearchParams();
  if (filters.companyId) {
    params.append('company_id', filters.companyId);
  }
  if (filters.dates?.fromDate) {
    params.append('start_date', filters.dates.fromDate.format('YYYY-MM-DD'));
  }
  if (filters.dates?.toDate) {
    params.append('end_date', filters.dates.toDate.format('YYYY-MM-DD'));
  }

  return axios
    .get(`/admins/products/rcl/credit-lines/reports/long-tape/?${params.toString()}`, {
      responseType: 'blob',
    })
    .then(({ data }) => data);
}

export function adminGetCreditLineDetail(creditLineId: string) {
  return axios
    .get<CreditLineDetail>(`/admins/products/rcl/credit-lines/${creditLineId}/`)
    .then(({ data }) => data);
}

export function adminCreateCreditLine(creditLineData: CreateCreditLinePayload) {
  return axios
    .post<CreditLine>(`/admins/products/rcl/credit-lines/`, creditLineData)
    .then(({ data }) => data);
}

export function adminUpdateCreditLine(creditLineId: string, creditLineData: EditCreditLinePayload) {
  return axios
    .patch<CreditLine>(`/admins/products/rcl/credit-lines/${creditLineId}/`, creditLineData)
    .then(({ data }) => data);
}

export function adminBlockCreditLine(creditLineId: string) {
  return axios
    .post<CreditLine>(`/admins/products/rcl/credit-lines/${creditLineId}/block/`)
    .then(({ data }) => data);
}

export function adminActivateCreditLine(creditLineId: string) {
  return axios
    .post<CreditLine>(`/admins/products/rcl/credit-lines/${creditLineId}/activate/`)
    .then(({ data }) => data);
}

export function adminDeactivateCreditLine(creditLineId: string) {
  return axios
    .post<CreditLine>(`/admins/products/rcl/credit-lines/${creditLineId}/deactivate/`)
    .then(({ data }) => data);
}

export function adminGetInterestPayments(page: number, pageSize: number, filters: any) {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  if (filters.month) {
    // Convert month to start and end of that month (dates only, no times)
    const monthStart = filters.month.startOf('month');
    const monthEnd = filters.month.endOf('month');
    params.append('start_date', monthStart.format('YYYY-MM-DD'));
    params.append('end_date', monthEnd.format('YYYY-MM-DD'));
  }
  filters.search && params.append('search', filters.search);
  filters.status && params.append('status', filters.status);

  return axios
    .get<InterestPaymentsResponse>(`/admins/products/rcl/monthly-charges/?${params.toString()}`)
    .then(({ data }) => data);
}

export function adminGetInterestPaymentDetail(interestPaymentId: string) {
  return axios
    .get<InterestPaymentDetail>(`/admins/products/rcl/monthly-charges/${interestPaymentId}/`)
    .then(({ data }) => data);
}

export function adminGetPayments(page: number, pageSize: number, filters: any) {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  if (filters.fromDate) {
    params.append('start_date', filters.fromDate.startOf('day').toISOString());
  }
  if (filters.toDate) {
    params.append('end_date', filters.toDate.endOf('day').toISOString());
  }
  filters.search && params.append('search', filters.search);
  filters.status && params.append('status', filters.status);

  return axios
    .get<PaymentsResponse>(`/admins/products/rcl/payments/?${params.toString()}`)
    .then(({ data }) => data);
}

export function adminGetPaymentDetail(paymentId: string) {
  return axios.get<PaymentDetail>(`/admins/products/rcl/payments/${paymentId}/`).then(({ data }) => data);
}

export function adminGetWithdrawals(page: number, pageSize: number, filters: any) {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  if (filters.fromDate) {
    params.append('start_date', filters.fromDate.startOf('day').toISOString());
  }
  if (filters.toDate) {
    params.append('end_date', filters.toDate.endOf('day').toISOString());
  }
  filters.search && params.append('search', filters.search);
  filters.status && params.append('status', filters.status);

  return axios
    .get<WithdrawalsResponse>(`/admins/products/rcl/draws/?${params.toString()}`)
    .then(({ data }) => data);
}

export function adminGetWithdrawalDetail(withdrawalId: string) {
  return axios.get<WithdrawalDetail>(`/admins/products/rcl/draws/${withdrawalId}/`).then(({ data }) => data);
}

export function adminApproveWithdrawal(withdrawalId: string) {
  return axios.post(`/admins/products/rcl/draws/${withdrawalId}/approve/`).then(({ data }) => data);
}

export function adminRejectWithdrawal(withdrawalId: string, rejectionReason: string) {
  return axios
    .post(`/admins/products/rcl/draws/${withdrawalId}/reject/`, { rejectionReason })
    .then(({ data }) => data);
}

export function adminCreateManualPayment(paymentData: CreateManualPaymentPayload) {
  return axios.post<Payment>(`/admins/products/rcl/payments/`, paymentData).then(({ data }) => data);
}

export function adminInitializeBankingAccount(companyId: number): Promise<{
  accountId: number;
}> {
  return axios
    .post(`/admins/companies/${companyId}/products/banking/initialize_account/`)
    .then(({ data }) => data);
}

interface BillingEngineChargesFilters {
  status?: string;
  search?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
}

export function adminGetBillingEngineCharges(
  companyId: number,
  page: number = 1,
  filters: BillingEngineChargesFilters = {}
): Promise<BillingEngineChargesResponse> {
  return axios
    .get(`/admins/companies/${companyId}/products/billing-engine/charges/`, {
      params: { page, ...filters },
    })
    .then(({ data }) => data);
}
